import React, { useCallback, useState } from 'react';
import { FirebaseEvent } from 'shared/types/appTypes';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Dialog,
  Button,
  Container,
  Divider,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { formatDateTzNaive } from 'src/utils/formatDateTzNaive';
import FormContent from './FormContent';
import useClientForm from 'src/hooks/useClientForm';

interface ClientEventFormDialogProps {
  open: boolean;
  onClose: () => void;
  event: FirebaseEvent | null;
  initialQuestionIndex?: number;
}

const ClientEventFormDialog: React.FC<ClientEventFormDialogProps> = ({ open, onClose, event, initialQuestionIndex }) => {
  const { fields, isLoading, isSaving, saveField, getFieldValue, setFieldValue } = useClientForm(event?.id);
  const [isDirty, setIsDirty] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const handleSave = useCallback(
    async (fieldIndex: number) => {
      await saveField(fieldIndex);
    },
    [saveField]
  );

  const handleComplete = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleCloseAttempt = useCallback(() => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleConfirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    onClose();
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setShowCloseConfirm(false);
  }, []);

  if (!event) return null;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleCloseAttempt}
      slotProps={{
        paper: {
          sx: {
            bgcolor: 'background.default',
            backgroundImage: 'none'
          }
        }
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          py: { xs: 2, sm: 3 }
        }}
      >
        {/* Close button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button onClick={handleCloseAttempt} size="small" sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </Box>

        {/* Event header */}
        <Stack spacing={2} sx={{ flexShrink: 0 }}>
          <Typography variant="h5">{event.friendlyTitle || event.dayliteData?.name || 'Untitled Event'}</Typography>

          <Divider />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {event.dateISO8601 && (
              <Chip label={formatDateTzNaive(event.dateISO8601)} color="primary" variant="outlined" />
            )}
            {event.venueInfo && <Chip icon={<LocationOnIcon />} label={event.venueInfo} variant="outlined" />}
          </Box>
        </Stack>

        {/* Form content area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, mt: 3 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : fields.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography color="text.secondary">No form fields configured for this event.</Typography>
            </Box>
          ) : (
            <FormContent
              fields={fields}
              onSave={handleSave}
              onComplete={handleComplete}
              getFieldValue={getFieldValue}
              setFieldValue={setFieldValue}
              isSaving={isSaving}
              onDirtyChange={setIsDirty}
              initialIndex={initialQuestionIndex}
            />
          )}
        </Box>
      </Container>

      {/* Close confirmation dialog */}
      <Dialog
        open={showCloseConfirm}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Close without saving?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. If you close now, your changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmClose} variant="contained" color="warning">
            Close Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ClientEventFormDialog;
